// src/features/unit/components/UnitConverter.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Paper, Grid, TextField, Autocomplete, IconButton, Typography,
    Divider, useTheme, CircularProgress
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { unitConverterConfig } from '../config/unitConverterConfig';
import { convertUnits } from '../utils/conversionLogic';
import StyledFAQ from '../../common/components/StyledFAQ';
import { Unit } from '../types/unitConverterTypes';

interface UnitConverterProps {
    categoryId: string;
}

const UnitConverter: React.FC<UnitConverterProps> = ({ categoryId }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const categoryConfig = useMemo(() => unitConverterConfig[categoryId], [categoryId]);

    const [fromValue, setFromValue] = useState<string>('1');
    const [toValue, setToValue] = useState<string>('');
    const [fromUnit, setFromUnit] = useState<Unit | null>(null);
    const [toUnit, setToUnit] = useState<Unit | null>(null);
    const [lastChanged, setLastChanged] = useState<'from' | 'to'>('from');

    // Initialize units when component mounts or category changes
    useEffect(() => {
        if (categoryConfig && categoryConfig.units.length >= 2) {
            setFromUnit(categoryConfig.units[0]);
            setToUnit(categoryConfig.units[1]);
            setFromValue('1'); // Reset value on category change
            setLastChanged('from'); // Ensure 'from' is the source of truth initially
        } else {
            setFromUnit(null);
            setToUnit(null);
        }
    }, [categoryConfig]);

    // Perform conversion when fromValue or units change
    useEffect(() => {
        if (lastChanged === 'from' && fromUnit && toUnit) {
            const result = convertUnits(fromValue, fromUnit, toUnit);
            setToValue(result);
        }
    }, [fromValue, fromUnit, toUnit, lastChanged]);

    // Perform reverse conversion when toValue or units change
    useEffect(() => {
        if (lastChanged === 'to' && fromUnit && toUnit) {
            const result = convertUnits(toValue, toUnit, fromUnit);
            setFromValue(result);
        }
    }, [toValue, fromUnit, toUnit, lastChanged]);

    const handleFromValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        // Allow only numbers and a single decimal point
        if (/^\d*\.?\d*$/.test(value)) {
            setFromValue(value);
            setLastChanged('from');
        }
    };

    const handleToValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (/^\d*\.?\d*$/.test(value)) {
            setToValue(value);
            setLastChanged('to');
        }
    };

    const handleSwapUnits = () => {
        // Swap both units and values
        const tempUnit = fromUnit;
        const tempValue = fromValue;
        
        setFromUnit(toUnit);
        setToUnit(tempUnit);
        setFromValue(toValue);
        setToValue(tempValue);
        
        // After swapping, set 'from' as the source of truth to maintain consistency
        setLastChanged('from');
    };

    const unitOptions = useMemo(() =>
            categoryConfig?.units.map(u => ({
                ...u,
                label: t(u.nameKey, { ns: 'tools' }),
                symbol: t(u.symbolKey, { ns: 'tools' })
            })) || [],
        [categoryConfig, t]);

    const getUnitOption = (unit: Unit | null): typeof unitOptions[0] | null => {
        if (!unit) return null;
        return unitOptions.find(opt => opt.id === unit.id) || null;
    }

    if (!categoryConfig) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>{t('unit_common_loading', { ns: 'tools' })}</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* The Main Converter UI */}
            <Paper elevation={3} sx={{
                p: { xs: 2, md: 4 },
                borderRadius: '16px',
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`
            }}>
                <Grid container alignItems="center" justifyContent="center" spacing={2}>
                    {/* From Section */}
                    <Grid item xs={12} md={5} {...{} as any}>
                        <TextField
                            fullWidth
                            label={t('unit_common_from_label', { ns: 'tools' })}
                            value={fromValue}
                            onChange={handleFromValueChange}
                            variant="outlined"
                            type="text"
                            inputMode="decimal"
                            InputProps={{
                                sx: { fontSize: '1.5rem', fontWeight: 'bold' }
                            }}
                        />
                        <Autocomplete
                            value={getUnitOption(fromUnit) as any}
                            onChange={(event, newValue) => setFromUnit(newValue)}
                            options={unitOptions}
                            getOptionLabel={(option) => `${option.label} (${option.symbol})`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('unit_common_select_unit', { ns: 'tools' })} />}
                            disableClearable
                            sx={{ mt: 1 }}
                        />
                    </Grid>

                    {/* Swap Icon */}
                    <Grid item xs={12} md={2} sx={{ textAlign: 'center' }} {...{} as any}>
                        <IconButton
                            onClick={handleSwapUnits}
                            aria-label={t('unit_common_swap_aria', { ns: 'tools' })}
                            sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                transform: { xs: 'rotate(90deg)', md: 'none' },
                                my: { xs: 1, md: 0 },
                            }}
                        >
                            <SwapHorizIcon color="primary" />
                        </IconButton>
                    </Grid>

                    {/* To Section */}
                    <Grid item xs={12} md={5} {...{} as any}>
                        <TextField
                            fullWidth
                            label={t('unit_common_to_label', { ns: 'tools' })}
                            value={toValue}
                            onChange={handleToValueChange}
                            variant="outlined"
                            type="text"
                            inputMode="decimal"
                            InputProps={{
                                sx: { fontSize: '1.5rem', fontWeight: 'bold' },
                                readOnly: false,
                            }}
                        />
                        <Autocomplete
                            value={getUnitOption(toUnit) as any}
                            onChange={(event, newValue) => {
                                setToUnit(newValue);
                                setLastChanged('from'); // Prioritize 'from' field after direct unit change
                            }}
                            options={unitOptions}
                            getOptionLabel={(option) => `${option.label} (${option.symbol})`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('unit_common_select_unit', { ns: 'tools' })} />}
                            disableClearable
                            sx={{ mt: 1 }}
                        />
                    </Grid>
                </Grid>

                {(fromUnit && toUnit) && (
                    <>
                        <Divider sx={{ my: 4 }} />
                        <Box>
                            <Typography variant="h6" gutterBottom>{t(fromUnit.nameKey, { ns: 'tools' })}</Typography>
                            <Typography variant="body2" color="text.secondary">{t(fromUnit.descKey, { ns: 'tools' })}</Typography>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>{t(toUnit.nameKey, { ns: 'tools' })}</Typography>
                            <Typography variant="body2" color="text.secondary">{t(toUnit.descKey, { ns: 'tools' })}</Typography>
                        </Box>
                    </>
                )}
            </Paper>

            {/* Information Section */}
            <Box sx={{ my: 10 }}>
                {categoryConfig.explanation && (
                    <Box sx={{ mb: 8, textAlign: 'center' }}>
                        <Typography variant="h4" component="h2" gutterBottom>
                            {t(categoryConfig.explanation.titleKey, { ns: 'tools' })}
                        </Typography>
                        <Typography sx={{ maxWidth: '700px', mx: 'auto' }} color="text.secondary">
                            {t(categoryConfig.explanation.descKey, { ns: 'tools' })}
                        </Typography>
                    </Box>
                )}
                {categoryConfig.whyUs && (
                    <Box sx={{ mb: 8, textAlign: 'center' }}>
                        <Typography variant="h4" component="h2" gutterBottom>
                            {t(categoryConfig.whyUs.titleKey, { ns: 'tools' })}
                        </Typography>
                        <Typography sx={{ maxWidth: '700px', mx: 'auto' }} color="text.secondary">
                            {t(categoryConfig.whyUs.descKey, { ns: 'tools' })}
                        </Typography>
                    </Box>
                )}
                <StyledFAQ
                    title={t('faq_title', { ns: 'tools' })}
                    faqs={categoryConfig.faqs}
                    t={(key) => t(key, { ns: 'tools' })}
                />
            </Box>
        </Box>
    );
};

export default UnitConverter;